<template>
  <div class="h-full bg-[#f8fafc] p-4 overflow-y-auto custom-scroll">
    <div class="print-hide bg-white rounded-xl shadow-sm border border-slate-200 border-t-4 border-t-[#0d4169] transition-all overflow-hidden mb-6">
      <div class="px-6 pt-5 pb-3">
        <h2 class="text-base font-bold text-[#0d4169]">Gross Profit Report</h2>
      </div>

      <form @submit.prevent="generateReport" class="px-6 pb-6 pt-2 border-b border-slate-100">
        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-3">
          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">
              Start Date <span class="text-red-500">*</span>
            </label>
            <input
              v-model="filters.startDate"
              required
              type="date"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
            />
          </div>

          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">
              End Date <span class="text-red-500">*</span>
            </label>
            <input
              v-model="filters.endDate"
              required
              type="date"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm"
            />
          </div>

          <div class="flex flex-col gap-1 text-left">
            <label class="text-[0.78rem] font-semibold text-[#1e293b]">Department</label>
            <select
              v-model="filters.departmentId"
              class="px-2.5 py-1.5 border border-[#cbd5e1] rounded-md outline-none focus:border-[#0d4169] transition-all text-sm bg-white cursor-pointer hover:bg-slate-50"
            >
              <option value="">All Departments</option>
              <option v-for="dep in departments" :key="dep.id" :value="String(dep.id)">
                {{ dep.name }}
              </option>
            </select>
          </div>
        </div>

        <div class="mt-6 flex justify-between items-center gap-4">
          <button
            type="button"
            @click="handlePrintReport"
            class="px-4 py-1.5 bg-slate-200 text-slate-700 text-sm font-bold rounded-md shadow-md hover:bg-slate-300 transition-all active:scale-95 disabled:opacity-60 flex items-center gap-2"
            :disabled="loading"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M6 9V2h12v7"/>
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
              <rect x="6" y="14" width="12" height="8"/>
            </svg>
            <span>Print</span>
          </button>
          <button
            type="submit"
            class="px-8 py-2 bg-[#0d4169] text-white text-sm font-bold rounded-md shadow-md hover:bg-[#0a3354] transition-all active:scale-95 disabled:opacity-60 flex items-center gap-2"
            :disabled="!filters.startDate || !filters.endDate || loading"
          >
            <span v-if="loading" class="w-4 h-4 border-2 border-white/70 border-t-transparent rounded-full animate-spin"></span>
            <span>{{ loading ? '' : 'Generate' }}</span>
          </button>
        </div>
      </form>
    </div>

    <div class="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden pr-3 pl-3 print-container">
      <div class="hidden print:block mb-6">
        <div class="print-doc-header print-doc-header-grid">
          <div class="print-doc-left">
            <div class="print-company-block">
              <img
                v-if="printCompanyLogo"
                :src="printCompanyLogo"
                :alt="printCompanyName || 'Company Logo'"
                class="print-company-logo"
              />
              <div v-else class="print-company-logo-fallback">Logo</div>
            </div>
          </div>
          <div class="print-doc-title-wrap">
            <div class="print-company-name print-company-name-title">{{ printCompanyName }}</div>
            <h1 class="print-doc-title text-xl font-bold text-center mb-2">Gross Profit Report</h1>
          </div>
          <div class="print-doc-meta print-doc-right">
            <div><strong>Date:</strong> {{ new Date().toLocaleDateString() }}</div>
          </div>
        </div>
        <hr class="my-4 border-black" />
      </div>
      <div class="relative min-h-[280px]">
        <div
          v-if="rows.length === 0"
          class="absolute inset-0 z-20 flex items-center justify-center text-center pointer-events-none"
        >
          <div class="text-slate-400 italic text-sm">
            <div class="text-5xl mb-4 opacity-20">📄</div>
            No records to display.<br />
            Select filters and click <span class="text-[#0d4169] font-bold">"Generate"</span>
          </div>
        </div>
        <div class="relative overflow-x-auto min-h-[280px]">
          <table class="w-full border-collapse bg-white min-w-max print-table">
            <thead class="sticky top-0 z-10 bg-[#f8fafc] shadow-sm">
              <tr>
                <th class="px-2 py-1 text-left font-bold text-[#64748b] border-b text-[11px] sticky-department">Department</th>
                <th class="px-2 py-1 text-center font-bold text-[#64748b] border-b text-[11px]">BBF</th>
                <th class="px-2 py-1 text-center font-bold text-[#64748b] border-b text-[11px]">Recieved from Store</th>
                <th class="px-2 py-1 text-center font-bold text-[#64748b] border-b text-[11px]">Direct Purchase</th>
                <th class="px-2 py-1 text-center font-bold text-[#64748b] border-b text-[11px]">Transfer In</th>
                <th class="px-2 py-1 text-center font-bold text-[#64748b] border-b text-[11px]">Available For Sale</th>
                <th class="px-2 py-1 text-center font-bold text-[#64748b] border-b text-[11px]">Ending Inv.</th>
                <th class="px-2 py-1 text-center font-bold text-[#64748b] border-b text-[11px]">Magmt Allowance</th>
                <th class="px-2 py-1 text-center font-bold text-[#64748b] border-b text-[11px]">Company Expense</th>
                <th class="px-2 py-1 text-center font-bold text-[#64748b] border-b text-[11px]">Room Service</th>
                <th class="px-2 py-1 text-center font-bold text-[#64748b] border-b text-[11px]">Spoilege &amp; Damege</th>
                <th class="px-2 py-1 text-center font-bold text-[#64748b] border-b text-[11px]">Transfer Out</th>
              <th class="px-2 py-1 text-center font-bold text-[#64748b] border-b text-[11px]">Others</th>
                <th class="px-2 py-1 text-center font-bold text-[#64748b] border-b text-[11px]">Total Less</th>
                <th class="px-2 py-1 text-center font-bold text-[#64748b] border-b text-[11px]">Net Cost</th>
                <th class="px-2 py-1 text-center font-bold text-[#64748b] border-b text-[11px]">Net Sales</th>
                <th class="px-2 py-1 text-center font-bold text-[#64748b] border-b text-[11px]">Gross Profit</th>
                <th class="px-2 py-1 text-center font-bold text-[#64748b] border-b text-[11px]">Cost %</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in rows"
                :key="row.departmentId"
                class="border-b last:border-0 hover:bg-slate-50 transition-colors"
              >
                <td class="px-2 py-1 text-left font-semibold text-[#0d4169] text-[11px] sticky-department">{{ row.departmentName }}</td>
                <td class="px-2 py-1 text-center text-slate-700 text-[11px]">{{ money(row.bbf) }}</td>
                <td class="px-2 py-1 text-center text-slate-700 text-[11px]">{{ money(row.recievedFromStore) }}</td>
                <td class="px-2 py-1 text-center text-slate-700 text-[11px]">{{ money(row.directPurchase) }}</td>
                <td class="px-2 py-1 text-center text-slate-700 text-[11px]">{{ money(row.transferIn) }}</td>
                <td class="px-2 py-1 text-center text-slate-700 text-[11px]">{{ money(row.tForSale) }}</td>
                <td class="px-2 py-1 text-center text-slate-700 text-[11px]">{{ money(row.endingInv) }}</td>
                <td class="px-2 py-1 text-center text-slate-700 text-[11px]">{{ money(row.magmtAllowance) }}</td>
                <td class="px-2 py-1 text-center text-slate-700 text-[11px]">{{ money(row.companyExpense) }}</td>
                <td class="px-2 py-1 text-center text-slate-700 text-[11px]">{{ money(row.roomService) }}</td>
                <td class="px-2 py-1 text-center text-slate-700 text-[11px]">{{ money(row.spoilegeAndDamege) }}</td>
                <td class="px-2 py-1 text-center text-slate-700 text-[11px]">{{ money(row.transferOut) }}</td>
              <td class="px-2 py-1 text-center text-slate-700 text-[11px]">{{ money(row.others) }}</td>
                <td class="px-2 py-1 text-center text-slate-700 text-[11px]">{{ money(row.totalLess) }}</td>
                <td class="px-2 py-1 text-center text-slate-700 text-[11px]">{{ money(row.netCost) }}</td>
                <td class="px-2 py-1 text-center text-slate-700 text-[11px]">{{ money(row.netSales) }}</td>
                <td class="px-2 py-1 text-center text-slate-700 text-[11px]">{{ money(row.grossProfit) }}</td>
                <td class="px-2 py-1 text-center text-slate-700 text-[11px]">{{ percent(row.costPercent) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import store from '../../../store'
import { apiFetch } from '../../../utils/api'

const currentUser = computed(() => store.getters.currentUser)
const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company_id || currentUser.value?.company?.id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch_id || currentUser.value?.branch?.id || null)
const printCompanyName = computed(() =>
  currentUser.value?.company?.name ||
  currentUser.value?.companyName ||
  currentUser.value?.company_name ||
  'Company'
)
const printCompanyLogo = computed(() =>
  currentUser.value?.company?.logo_url ||
  currentUser.value?.companyLogo ||
  currentUser.value?.company_logo ||
  ''
)

const departments = ref([])
const rows = ref([])
const loading = ref(false)

const filters = ref({
  startDate: '',
  endDate: '',
  departmentId: '',
})

// function toLocalISODate(date = new Date()) {
//   const yyyy = date.getFullYear()
//   const mm = String(date.getMonth() + 1).padStart(2, '0')
//   const dd = String(date.getDate()).padStart(2, '0')
//   return `${yyyy}-${mm}-${dd}`
// }

function money(val) {
  const n = Number(String(val ?? 0).replace(/,/g, ''))
  const x = Number.isFinite(n) ? n : 0
  return x.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function percent(val) {
  const n = Number(val ?? 0)
  const x = Number.isFinite(n) ? n : 0
  return `${x.toFixed(2)}%`
}

function handlePrintReport() {
  if (!rows.value.length) {
    alert('No data to print. Please generate the report first.')
    return
  }
  window.print()
}

async function loadDepartments() {
  if (!companyId.value && !branchId.value) return
  try {
    let endpoint = `/departments/company/${companyId.value}`
    if (branchId.value) endpoint = `/departments/branch/${branchId.value}`
    const res = await apiFetch(endpoint)
    if (res.ok) {
      const data = await res.json().catch(() => [])
      const list = Array.isArray(data) ? data : []
      departments.value = list.filter((d) => {
        const isCentral = d.isCentral ?? d.is_central
        return isCentral === 0 || isCentral === false
      })
      departments.value.sort((a, b) => a.name.localeCompare(b.name))
    }
  } catch (err) {
    console.error('Failed to load departments', err)
  }
}

async function generateReport() {
  if (!filters.value.startDate || !filters.value.endDate) return
  loading.value = true
  try {
    const query = new URLSearchParams()
    if (branchId.value) query.append('branchId', String(branchId.value))
    else if (companyId.value) query.append('companyId', String(companyId.value))
    query.append('startDate', String(filters.value.startDate))
    query.append('endDate', String(filters.value.endDate))
    if (filters.value.departmentId) query.append('departmentId', String(filters.value.departmentId))

    const res = await apiFetch(`/gross-profit-report?${query.toString()}`)
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      alert(err?.message || 'Failed to generate gross profit report.')
      rows.value = []
      return
    }

    const data = await res.json().catch(() => [])
    const allowedDeptIds = new Set(
      departments.value.map((d) => Number(d.id)).filter((id) => Number.isFinite(id) && id > 0)
    )
    rows.value = (Array.isArray(data) ? data : [])
      .filter((row) => allowedDeptIds.has(Number(row.departmentId)))
      .slice()
      .sort((a, b) => String(a.departmentName).localeCompare(String(b.departmentName)))
  } catch (err) {
    console.error('Failed to generate gross profit report', err)
    alert('Failed to generate gross profit report.')
    rows.value = []
  } finally {
    loading.value = false
  }
}

onMounted(async () => {
  filters.value.startDate = ""
  await loadDepartments()
})
</script>

<style scoped>
.sticky-department {
  position: sticky;
  left: 0;
  z-index: 12;
  background: #ffffff;
  background-clip: padding-box;
}

thead .sticky-department {
  z-index: 22;
  background: #f8fafc;
}

@media print {
  :global(.sidebar),
  :global(header),
  :global(nav),
  :global(aside),
  :global(.top-bar),
  :global(.app-header),
  :global(.logout-btn) {
    display: none !important;
  }

  .print-hide {
    display: none !important;
  }

  body, .bg-\[\#f8fafc\] {
    background: transparent !important;
  }

  :global(.md\:ml-\[150px\]) {
    margin-left: 0 !important;
  }

  .overflow-x-auto {
    overflow: visible !important;
    min-height: auto !important;
  }

  .print-container {
    box-shadow: none !important;
    border: none !important;
    width: 100% !important;
    max-width: 100% !important;
    overflow: visible !important;
  }

  .print-table {
    width: 100% !important;
    min-width: 0 !important;
    border-collapse: collapse !important;
    border-spacing: 0 !important;
    table-layout: auto !important;
  }

  .print-table th,
  .print-table td {
    border: 0 !important;
    border-right: 1px solid #000 !important;
    border-bottom: 1px solid #000 !important;
    padding: 1px 2px !important;
    text-align: left !important;
    color: #000 !important;
    font-size: 8px !important;
    white-space: normal !important;
    word-break: break-word !important;
    overflow-wrap: anywhere !important;
    line-height: 1.15 !important;
  }

  .print-table tr > *:first-child {
    border-left: 1px solid #000 !important;
  }

  .print-table thead tr:first-child > * {
    border-top: 1px solid #000 !important;
  }

  .print-table th {
    background-color: #f3f3f3 !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
    font-size: 7.5px !important;
    text-align: center !important;
    position: static !important;
    top: auto !important;
    overflow: visible !important;
  }

  .print-table thead {
    position: static !important;
  }

  .print-table thead tr,
  .print-table tbody tr {
    page-break-inside: avoid !important;
  }

  .sticky-department {
    position: static !important;
    left: auto !important;
    z-index: auto !important;
    background: transparent !important;
    white-space: nowrap !important;
    word-break: normal !important;
    overflow-wrap: normal !important;
  }

  .print-doc-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
  }

  .print-doc-header-grid {
    display: grid;
    grid-template-columns: 1fr auto 1fr;
    align-items: center;
    column-gap: 12px;
  }

  .print-doc-left {
    justify-self: start;
  }

  .print-doc-title-wrap {
    justify-self: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
  }

  .print-doc-title {
    justify-self: center;
    margin: 0 !important;
    white-space: nowrap;
  }

  .print-doc-right {
    justify-self: end;
  }

  .print-company-block {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
  }

  .print-company-logo {
    width: 52px;
    height: 52px;
    min-width: 52px;
    min-height: 52px;
    max-width: 52px;
    max-height: 52px;
    flex: 0 0 52px;
    display: block;
    object-fit: contain;
    border: 0 !important;
    border-radius: 0;
    padding: 0;
    background: #fff;
  }

  .print-company-logo-fallback {
    width: 52px;
    height: 52px;
    min-width: 52px;
    min-height: 52px;
    max-width: 52px;
    max-height: 52px;
    flex: 0 0 52px;
    border: 1px solid #000;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    color: #334155;
    background: #fff;
  }

  .print-company-name {
    font-size: 18px;
    font-weight: 700;
    color: #0f172a;
    line-height: 1.2;
  }

  .print-company-name-title {
    font-size: 16px;
    text-align: center;
  }

  .print-doc-meta {
    font-size: 12px;
    color: #0f172a;
    white-space: nowrap;
  }
}
</style>
